/* ==========================================================================
   CART (Dawn-compatible) – realtime update line totals & footer totals
   ========================================================================== */

/* --------------------------- Remove button --------------------------- */

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems && cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}
if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

/* --------------------------- Utilities --------------------------- */

function __setHTML(target, html) {
  if (!target) return;
  // Nếu theme có HTMLUpdateUtility (giữ script)
  if (window.HTMLUpdateUtility && typeof HTMLUpdateUtility.setInnerHTML === 'function') {
    HTMLUpdateUtility.setInnerHTML(target, html);
  } else {
    target.innerHTML = html;
  }
}

// Định dạng tiền tệ an toàn cho mọi theme
function __formatMoneySafe(cents) {
  try {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const fmt =
        (window.theme && theme.moneyFormat) ||
        (window.Shopify && Shopify.money_format) ||
        '{{amount_no_decimals_with_comma_separator}} VND';
      return Shopify.formatMoney(cents, fmt);
    }
  } catch (e) {}
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString('vi-VN') + ' ₫';
}

// Cập nhật con số “Tổng số tiền ước tính” trực tiếp trên DOM
function __updateEstimatedTotalUI(state) {
  if (!state) return;
  const value = state.total_price ?? state.items_subtotal_price ?? 0;

  // 1) selector chính được khuyến nghị
  const el = document.querySelector('[data-cart-estimated-total]');
  if (el) el.innerHTML = __formatMoneySafe(value);

  // 2) fallback – các class thường gặp trong footer
  const guess =
    document.querySelector('#main-cart-footer .totals__total-value') ||
    document.querySelector('#main-cart-footer .totals__subtotal-value') ||
    document.querySelector('.cart__footer .totals__total-value') ||
    document.querySelector('.cart__footer .totals__subtotal-value');
  if (!el && guess) guess.innerHTML = __formatMoneySafe(value);
  const drawerTotal =
    document.querySelector('#CartDrawer .totals__total-value') ||
    document.querySelector('.drawer__footer .totals__total-value');
  if (drawerTotal) drawerTotal.innerHTML = __formatMoneySafe(value);
}

/* --------------------------- Cart Items --------------------------- */

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    // debounce theo Dawn constants.js
    const debouncedOnChange = debounce((event) => this.onChange(event), ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange);
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  /* ---------------------- Handlers & validation ---------------------- */

  onChange(event) {
    this.validateQuantity(event);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select?.();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (Number.isNaN(inputValue)) return;

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (event.target.max && inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (parseInt(event.target.step || '1') && inputValue % parseInt(event.target.step || '1') !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
      return;
    }

    event.target.setCustomValidity('');
    event.target.reportValidity();

    this.updateQuantity(
      index,
      inputValue,
      event,
      document.activeElement?.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  /* ---------------------- Cross-tab/cart-drawer re-render ---------------------- */

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          selectors.forEach((selector) => {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          });
        })
        .catch((e) => console.error(e));
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((r) => r.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
          // Đồng bộ footer (nếu section footer không đổi)
          fetch(`${routes.cart_url}.json`)
            .then((r) => r.json())
            .then(__updateEstimatedTotalUI)
            .catch(() => {});
        })
        .catch((e) => console.error(e));
    }
  }

  /* ---------------------- Sections to render ---------------------- */

  getSectionsToRender() {
    const out = [];

    // 1) Bảng line items
    const itemsEl = document.getElementById('main-cart-items');
    if (itemsEl?.dataset?.id) {
      out.push({
        id: 'main-cart-items',
        section: itemsEl.dataset.id,
        selector: '.js-contents',
      });
    }

    // 2) Footer / totals – phần "Tổng số tiền ước tính"
    const footerEl = document.getElementById('main-cart-footer');
    if (footerEl?.dataset?.id) {
      out.push({
        id: 'main-cart-footer',
        section: footerEl.dataset.id,
        selector: '.js-contents',
      });
    }

    // 3) Bubble số lượng trên icon giỏ (nếu có)
    const bubbleMounted = document.getElementById('shopify-section-cart-icon-bubble');
    if (bubbleMounted) {
      out.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      });
    }

    // 4) Live region (Dawn dùng để đọc màn hình)
    const liveRegion = document.getElementById('cart-live-region-text');
    if (liveRegion) {
      out.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    return out;
  }

  /* ---------------------- Update quantity (AJAX) ---------------------- */

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    });

    const eventTarget = event?.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((stateText) => {
        const parsedState = JSON.parse(stateText);

        // Lỗi từ Shopify
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Empty states
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Re-render tất cả sections cần thiết
        this.getSectionsToRender().forEach((section) => {
          const rootEl = document.getElementById(section.id);
          const container = section.selector
            ? (document.querySelector(section.selector) || rootEl?.querySelector(section.selector))
            : rootEl;

          const html = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          if (container && html != null) __setHTML(container, html);
        });

        // Ép cập nhật “Tổng số tiền ước tính” trực tiếp từ state trả về
        this.updateEstimatedTotalFromSections(parsedState);

        // HARD FIX: nếu footer dùng class khác (.totals__total-value / .totals__subtotal-value)
        try {
          const footerConf = this.getSectionsToRender().find((s) => s.id === 'main-cart-footer');
          if (footerConf && parsedState.sections?.[footerConf.section]) {
            const tmpDoc = new DOMParser().parseFromString(parsedState.sections[footerConf.section], 'text/html');
            const newHTML =
              tmpDoc.querySelector('.totals__total-value')?.innerHTML ||
              tmpDoc.querySelector('.totals__subtotal-value')?.innerHTML;
            const target =
              document.querySelector('#main-cart-footer .totals__total-value') ||
              document.querySelector('#main-cart-footer .totals__subtotal-value');
            if (newHTML && target) target.innerHTML = newHTML;
          }
        } catch (err) {
          console.warn('Could not hard-update estimated total:', err);
        }

        // Live regions / focus management
        const items = document.querySelectorAll('.cart-item');
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && quantityElement) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else if (updatedValue !== parseInt(quantityElement.value)) {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`)?.focus?.();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        // Pub/Sub – cho nơi khác (sticky cart, header…) biết
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });

        // Đồng bộ fallback
        try { __updateEstimatedTotalUI(parsedState); } catch (e) {}
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((ov) => ov.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => this.disableLoading(line));
  }

  /* ---------------------- Helpers ---------------------- */

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const text = lineItemError.querySelector('.cart-item__error-text');
      if (text) text.textContent = message || '';
    }

    if (!this.lineItemStatusElement) return;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);
    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  // Lấy textContent của một selector từ HTML section trả về
  getSectionText(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.textContent : null;
  }

  // Cập nhật nhanh "Tổng số tiền ước tính" từ section footer trả về
  updateEstimatedTotalFromSections(parsedState) {
    try {
      const footerSection = this.getSectionsToRender().find((s) => s.id === 'main-cart-footer');
      if (!footerSection) return;

      const sectionHtml = parsedState.sections?.[footerSection.section];
      if (!sectionHtml) return;

      const newText =
        this.getSectionText(sectionHtml, '[data-cart-estimated-total]') ||
        this.getSectionText(sectionHtml, '.totals__total-value') ||
        this.getSectionText(sectionHtml, '.totals__subtotal-value');
      if (!newText) return;

      const node =
        document.querySelector('[data-cart-estimated-total]') ||
        document.querySelector('#main-cart-footer .totals__total-value') ||
        document.querySelector('#main-cart-footer .totals__subtotal-value');
      if (node) node.textContent = newText.trim();
    } catch (e) {
      console.warn('updateEstimatedTotalFromSections failed', e);
    }
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    [...cartItemElements, ...cartDrawerItemElements].forEach((ov) => ov.classList.remove('hidden'));

    document.activeElement?.blur?.();
    this.lineItemStatusElement?.setAttribute?.('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    cartItemElements.forEach((ov) => ov.classList.add('hidden'));
    cartDrawerItemElements.forEach((ov) => ov.classList.add('hidden'));
  }
}

/* Đăng ký custom element (không define lại nếu đã có) */
if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}


/* ===== Vá bổ sung cho Estimated Total trong các luồng khác ===== */

// Bảo đảm cập nhật khi nhận pub/sub cartUpdate (phòng trường hợp renderContents không được gọi)
if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
  subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
    if (cartData) __updateEstimatedTotalUI(cartData);
  });
}
